import React, { useEffect, useState } from "react";

function relativeTime(timestamp, now) {
  const value = new Date(timestamp).getTime();
  if (Number.isNaN(value)) return "just now";

  const seconds = Math.max(0, Math.round((now - value) / 1000));
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.round(seconds / 3600)} hr ago`;
  return `${Math.round(seconds / 86400)} day ago`;
}

function truncate(text, max = 140) {
  if (!text) return "";
  return text.length > max ? `${text.slice(0, max - 1)}…` : text;
}

function normalizeActivity(entry = {}) {
  return {
    id: entry.id || `${entry.label || "activity"}-${entry.timestamp || Date.now()}`,
    label: entry.label || "Workspace updated",
    detail: entry.detail || "",
    type: entry.type || "system",
    timestamp: entry.timestamp || new Date().toISOString()
  };
}

function normalizeFile(file = {}) {
  return {
    key: file.key || `${file.name || "file"}-${file.extension || "FILE"}`,
    name: file.name || "Unnamed file",
    extension: file.extension || "FILE",
    group: file.group || "Discussion workspace",
    state: file.state || "linked",
    timestamp: file.timestamp || new Date().toISOString(),
    message: file.message || ""
  };
}

function fileIconClass(extension) {
  const key = `${extension || ""}`.toLowerCase();
  if (["csv", "tsv"].includes(key)) return "bi bi-filetype-csv";
  if (["xlsx", "xls", "xlsm"].includes(key)) return "bi bi-file-earmark-spreadsheet";
  if (["json", "geojson"].includes(key)) return "bi bi-braces-asterisk";
  if (key === "zip") return "bi bi-file-zip";
  if (["pdf", "doc", "docx", "txt"].includes(key)) return "bi bi-file-earmark-richtext";
  if (["png", "jpg", "jpeg", "tif", "tiff"].includes(key)) return "bi bi-image";
  return "bi bi-file-earmark";
}

function stateLabel(state) {
  if (state === "linked") return "Linked";
  if (state === "staging") return "Staging";
  if (state === "staged") return "Ready";
  if (state === "error") return "Error";
  return "Queued";
}

function updateFiles(previousFiles, nextFile) {
  const normalized = normalizeFile(nextFile);
  if (["removed", "canceled"].includes(normalized.state)) {
    return previousFiles.filter((file) => file.key !== normalized.key);
  }

  const nextFiles = [...previousFiles];
  const existingIndex = nextFiles.findIndex((file) => file.key === normalized.key);

  if (existingIndex >= 0) {
    nextFiles[existingIndex] = {
      ...nextFiles[existingIndex],
      ...normalized
    };
  } else {
    nextFiles.unshift(normalized);
  }

  return nextFiles.slice(0, 14);
}

export default function DiscussionLiveSidebar({
  title = "Dataset discussion",
  status = "Queued",
  linkedFiles = [],
  initialActivity = []
}) {
  const [focus, setFocus] = useState("");
  const [draftPrompt, setDraftPrompt] = useState("");
  const [files, setFiles] = useState((linkedFiles || []).map((file) => normalizeFile(file)));
  const [activity, setActivity] = useState((initialActivity || []).map((entry) => normalizeActivity(entry)));
  const [now, setNow] = useState(Date.now());

  useEffect(() => {
    const intervalId = window.setInterval(() => setNow(Date.now()), 15000);
    return () => window.clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const appendActivity = (entry) => {
      const nextEntry = normalizeActivity(entry);
      setActivity((previous) => [nextEntry, ...previous].slice(0, 8));
    };

    const handleActivity = (event) => {
      appendActivity(event.detail || {});
    };

    const handleUploadState = (event) => {
      const detail = event.detail || {};
      if (Object.prototype.hasOwnProperty.call(detail, "focus")) setFocus(detail.focus || "");
      if (Object.prototype.hasOwnProperty.call(detail, "prompt")) setDraftPrompt(detail.prompt || "");
    };

    const handleUploadFileState = (event) => {
      setFiles((previous) => updateFiles(previous, event.detail || {}));
    };

    const handleDocumentClick = (event) => {
      const starter = event.target.closest("[data-discussion-starter]");
      if (starter) {
        appendActivity({
          label: "Starter selected",
          detail: starter.getAttribute("data-discussion-starter") || "",
          type: "prompt",
          timestamp: new Date().toISOString()
        });
        return;
      }

      const sendButton = event.target.closest("[data-discussion-send]");
      if (sendButton) {
        const composer = document.querySelector("[data-discussion-composer-input]");
        const draft = composer?.value?.trim() || "";
        if (!draft) return;

        setDraftPrompt(draft);
        appendActivity({
          label: "Draft message prepared",
          detail: truncate(draft, 120),
          type: "message",
          timestamp: new Date().toISOString()
        });
      }
    };

    const handleDocumentInput = (event) => {
      if (event.target.matches("[data-discussion-composer-input]")) {
        setDraftPrompt(event.target.value || "");
      }
    };

    window.addEventListener("discussion:activity", handleActivity);
    window.addEventListener("discussion:upload-state", handleUploadState);
    window.addEventListener("discussion:upload-file-state", handleUploadFileState);
    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("input", handleDocumentInput);

    return () => {
      window.removeEventListener("discussion:activity", handleActivity);
      window.removeEventListener("discussion:upload-state", handleUploadState);
      window.removeEventListener("discussion:upload-file-state", handleUploadFileState);
      document.removeEventListener("click", handleDocumentClick);
      document.removeEventListener("input", handleDocumentInput);
    };
  }, []);

  const linkedCount = files.filter((file) => file.state === "linked").length;
  const activeCount = files.filter((file) => ["staging", "staged", "error"].includes(file.state)).length;

  return (
    <div className="sub-discussion-live-sidebar">
      <section className="sub-discussion-live-panel">
        <h2>Activity Watch</h2>
        <p>This space is used to track and monitor the ongoing activity within the discussion. A list of all linked files to this discussion can be seen down below.</p>
        <div className="sub-discussion-live-metrics">
          <div className="sub-discussion-live-metric">
            <strong>{linkedCount}</strong>
            <span>Linked</span>
          </div>
          <div className="sub-discussion-live-metric">
            <strong>{activeCount}</strong>
            <span>Active</span>
          </div>
          <div className="sub-discussion-live-metric">
            <strong>{activity.length}</strong>
            <span>Signals</span>
          </div>
        </div>
      </section>

      <section className="sub-discussion-live-panel">
        <div className="sub-discussion-live-section-head">
          <h3>Current focus</h3>
          <span className="sub-discussion-live-status">{status}</span>
        </div>
        <p className="sub-discussion-live-focus">{focus || "Waiting for an upload focus or a starter prompt."}</p>
        <p className="sub-discussion-live-note">{truncate(draftPrompt, 180) || `The latest discussion draft for ${title} will surface here while you type.`}</p>
      </section>

      <section className="sub-discussion-live-panel">
        <div className="sub-discussion-live-section-head">
          <h3>File states</h3>
          <span>{files.length}</span>
        </div>
        {files.length ? (
          <ul className="sub-discussion-live-file-list">
            {files.map((file) => (
              <li key={file.key} className="sub-discussion-live-file-item">
                <span className="sub-discussion-live-file-icon">
                  <i className={fileIconClass(file.extension)} />
                </span>
                <span className="sub-discussion-live-file-copy">
                  <strong>{file.name}</strong>
                  <small>{file.group}</small>
                </span>
                <span className={`sub-discussion-live-file-state is-${file.state}`}>
                  {stateLabel(file.state)}
                </span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="sub-discussion-empty-state sub-discussion-empty-state--compact">
            <i className="bi bi-inboxes-fill" />
            <p>No file activity yet. Open the attachment tray when you need more source material.</p>
          </div>
        )}
      </section>

      <section className="sub-discussion-live-panel">
        <div className="sub-discussion-live-section-head">
          <h3>Recent activity</h3>
          <span>Auto-refresh</span>
        </div>
        <ul className="sub-discussion-live-activity-list">
          {activity.map((entry) => (
            <li key={entry.id} className={`sub-discussion-live-activity-item is-${entry.type}`}>
              <div>
                <strong>{entry.label}</strong>
                <p>{entry.detail}</p>
              </div>
              <span>{relativeTime(entry.timestamp, now)}</span>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}